import * as React from 'react'
import { cn } from '@workspace/ui/lib/utils'
import {
  getTypographyClasses,
  type MarketingTypographyVariant,
  type DashboardTypographyVariant,
  type TypographyColor,
} from '@workspace/ui/lib/typography'

interface BaseTypographyProps {
  children?: React.ReactNode
  className?: string
  color?: TypographyColor
  as?: React.ElementType
}

export interface MarketingTypographyProps extends BaseTypographyProps {
  variant: MarketingTypographyVariant
  // Allow any additional props for motion components, dangerouslySetInnerHTML, etc.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export interface DashboardTypographyProps extends BaseTypographyProps {
  variant: DashboardTypographyVariant
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export function MarketingTypography({
  variant,
  color = 'primary',
  className,
  children,
  as,
  ...props
}: MarketingTypographyProps): React.ReactElement {
  const Component = as || getDefaultComponent('marketing', variant)
  const classes = getTypographyClasses('marketing', variant, color)

  return React.createElement(
    Component,
    { className: cn(classes, className), ...props },
    children
  )
}

export function DashboardTypography({
  variant,
  color = 'primary',
  className,
  children,
  as,
  ...props
}: DashboardTypographyProps): React.ReactElement {
  const Component = as || getDefaultComponent('dashboard', variant)
  const classes = getTypographyClasses('dashboard', variant, color)

  return React.createElement(
    Component,
    { className: cn(classes, className), ...props },
    children
  )
}

// Unified Typography component with scale prop
interface TypographyProps extends BaseTypographyProps {
  scale: 'marketing' | 'dashboard'
  variant: MarketingTypographyVariant | DashboardTypographyVariant
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export function Typography({
  scale,
  variant,
  color = 'primary',
  className,
  children,
  as,
  ...props
}: TypographyProps): React.ReactElement {
  const Component = as || getDefaultComponent(scale, variant)
  const classes = getTypographyClasses(scale, variant as MarketingTypographyVariant | DashboardTypographyVariant, color)

  return React.createElement(
    Component,
    { className: cn(classes, className), ...props },
    children
  )
}

// Helper function to determine default HTML element based on variant
function getDefaultComponent(
  scale: 'marketing' | 'dashboard',
  variant: string
): React.ElementType {
  if (scale === 'marketing') {
    switch (variant) {
      case 'hero':
      case 'display':
      case 'heading1':
        return 'h1'
      case 'heading2':
        return 'h2'
      case 'heading3':
        return 'h3'
      case 'heading4':
        return 'h4'
      case 'subtitle':
      case 'body':
      case 'small':
        return 'p'
      case 'caption':
        return 'span'
      default:
        return 'span'
    }
  } else {
    switch (variant) {
      case 'pageTitle':
        return 'h1'
      case 'sectionTitle':
        return 'h2'
      case 'cardTitle':
        return 'h3'
      case 'heading':
        return 'h4'
      case 'label':
      case 'uppercase':
        return 'span'
      case 'body':
      case 'small':
        return 'p'
      case 'caption':
        return 'span'
      default:
        return 'span'
    }
  }
}

// Export individual preset components for convenience
export const MarketingHero = (props: Omit<MarketingTypographyProps, 'variant'>): React.ReactElement => (
  <MarketingTypography variant="hero" {...props} />
)

export const MarketingDisplay = (props: Omit<MarketingTypographyProps, 'variant'>): React.ReactElement => (
  <MarketingTypography variant="display" {...props} />
)

export const MarketingH1 = (props: Omit<MarketingTypographyProps, 'variant'>): React.ReactElement => (
  <MarketingTypography variant="heading1" {...props} />
)

export const MarketingH2 = (props: Omit<MarketingTypographyProps, 'variant'>): React.ReactElement => (
  <MarketingTypography variant="heading2" {...props} />
)

export const MarketingH3 = (props: Omit<MarketingTypographyProps, 'variant'>): React.ReactElement => (
  <MarketingTypography variant="heading3" {...props} />
)

export const MarketingH4 = (props: Omit<MarketingTypographyProps, 'variant'>): React.ReactElement => (
  <MarketingTypography variant="heading4" {...props} />
)

export const DashboardPageTitle = (props: Omit<DashboardTypographyProps, 'variant'>): React.ReactElement => (
  <DashboardTypography variant="pageTitle" {...props} />
)

export const DashboardSectionTitle = (props: Omit<DashboardTypographyProps, 'variant'>): React.ReactElement => (
  <DashboardTypography variant="sectionTitle" {...props} />
)

export const DashboardCardTitle = (props: Omit<DashboardTypographyProps, 'variant'>): React.ReactElement => (
  <DashboardTypography variant="cardTitle" {...props} />
)

export const DashboardLabel = (props: Omit<DashboardTypographyProps, 'variant'>): React.ReactElement => (
  <DashboardTypography variant="label" {...props} />
)

export const DashboardBody = (props: Omit<DashboardTypographyProps, 'variant'>): React.ReactElement => (
  <DashboardTypography variant="body" {...props} />
)
